import { Link, useLocation } from 'react-router-dom';
import SadCloudIllustration from '@app/assets/illustrations/sad-cloud.svg?react';
import { useState, useEffect } from 'react';
import { getCampaignPageDesigns } from '@app/services/campaigns';
import { CampaignPageDesignInterface } from '@app/models/campaigns';

interface ReviewData {
  review: string;
  rating: number;
  yelpUrl?: string | null;
  googleUrl?: string | null;
  businessName?: string;
  businessAddress?: string;
  thumbnails: string[];
  timestamp: string;
}

export const LeaveReviewBad = () => {
  const location = useLocation();
  const campaignId = location.pathname.split('/')[2]; // Gets the campaignId from /leave-review/{campaignId}/need-improvement
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  
  // State for dynamic design
  const [negativeWindowDesign, setNegativeWindowDesign] = useState<CampaignPageDesignInterface | null>(null);
  const [isLoadingDesign, setIsLoadingDesign] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [businessData, setBusinessData] = useState<{name: string, address: string} | null>(null);

  useEffect(() => {
    // Get review data from localStorage
    const storedData = localStorage.getItem('reviewData');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        setReviewData(data);
      } catch (error) {
        console.error('Error parsing review data:', error);
      }
    }

    // Cleanup function to remove review data when component unmounts
    return () => {
      localStorage.removeItem('reviewData');
    };
  }, []);

  // Extract access token from URL
  useEffect(() => {
    let token = null;
    
    // First, try standard query parameters
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get('t');
    
    // If not found, check if parameters are in the pathname
    if (!token) {
      const pathParts = window.location.pathname.split('/');
      const requestIdPart = pathParts[pathParts.length - 1];
      
      if (requestIdPart && requestIdPart.includes('&')) {
        const paramsString = requestIdPart.split('&').slice(1).join('&');
        const pathParams = new URLSearchParams(paramsString);
        token = pathParams.get('t');
      }
    }

    if (token) {
      setAccessToken(token);
    }
  }, []);

  // Fetch negative window design
  useEffect(() => {
    const fetchNegativeWindowDesign = async () => {
      if (!campaignId || !accessToken) {
        return;
      }
      
      setIsLoadingDesign(true);
      try {
        const response = await getCampaignPageDesigns(
          campaignId,
          accessToken,
          '' // Empty businessId for initial call
        );
        
        if (response.data.total > 0) {
          // Extract businessId from the first design's campaign data
          const firstDesign = response.data.pageDesigns[0];
          const extractedBusinessId = firstDesign?.campaign?.businessId;
          
          if (extractedBusinessId) {
            // Extract business data from the first design's campaign
            const business = (firstDesign?.campaign as any)?.business;
            if (business) {
              setBusinessData({
                name: business.name,
                address: business.address
              });
            }
            
            // Find negative window design
            const negativeDesign = response.data.pageDesigns.find(design => design.type === 'negativeWindowDesign');
            if (negativeDesign) {
              setNegativeWindowDesign(negativeDesign);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch negative window design:', error);
      } finally {
        setIsLoadingDesign(false);
      }
    };

    fetchNegativeWindowDesign();
  }, [campaignId, accessToken]);


  // Show loading state while fetching design
  if (isLoadingDesign) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-baseBlue mx-auto mb-4"></div>
          <p className="text-base-coolGray">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6 gap-6">
      <div className="bg-white text-center space-y-8 rounded max-w-[845px] w-full py-6 px-20 flex justify-center items-center flex-col mt-auto">
        <div className="flex items-center justify-center gap-2">
          <div
            className="w-12 h-12 flex items-center justify-center  rounded-lg shrink-0"
            style={{ color: `#424244`, background: `#42424414` }}
          >
            <span className="font-bold text-lg">{businessData?.name?.charAt(0) || reviewData?.businessName?.charAt(0) || 'D'}</span>
          </div>
          <div className="flex flex-col">
            <h5 className="font-bold mr-auto break-all text-lg leading-none">
              {businessData?.name || reviewData?.businessName || 'DevX'}
            </h5>
          </div>
        </div>
        <h4 className="font-extrabold text-4xl">
          {negativeWindowDesign?.heading || 'We are very sorry that you had a bad experience!'}
        </h4>
        <p className="text-base-coolGray text-base">
          {negativeWindowDesign?.description || 'We will do our best to correct the negative aspects you have identified. Thank you for being transparent with us!'}
        </p>


        <div className="w-fit">
          {negativeWindowDesign?.imagePath && negativeWindowDesign.imagePath !== null ? (
            <img 
              src={negativeWindowDesign.imagePath} 
              alt="Sorry illustration"
              className="max-w-full h-auto"
            />
          ) : (
            <SadCloudIllustration />
          )}
        </div>
      </div>

      <p className="mt-auto text-base-coolGray">
        Powered by
        <Link to="/sign-up" className="ml-2 text-brand-baseBlue font-bold">
          Echo Ratings
        </Link>
      </p>
    </div>
  );
};
